/**
 * The one read a shared enquiry form makes: who the practitioner is, and what
 * their form offers.
 *
 * Hand-rolled rather than a `useQuery`, unlike every other read in the app, and
 * for a plain reason — TanStack's provider is mounted by the dashboard's tree,
 * and this screen renders outside all of it. Nothing here may assume a
 * provider: no query client, no toasts, no Clerk, no workspace.
 *
 * Four outcomes, and the screen draws a different thing for each:
 *   loading   the read is still out
 *   ready     the form, with the brand to draw it under
 *   missing   404 — an unclaimed name, or sharing switched off
 *   failed    anything else: offline, a timeout, the server erroring
 *
 * `missing` is deliberately one state and not three. A stranger holding a link
 * should not be told whether it was ever real.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../lib/http";
import { FORMS_PREVIEW } from "../../../lib/site/config";
import { PREVIEW_FORM } from "../data/publicEnquiryPreview";
import { fetchPublicEnquiryForm } from "../services/publicEnquiryService";
import type { PublicEnquiryForm } from "../types/enquiries.types";

export type PublicFormStatus = "loading" | "ready" | "missing" | "failed";

export interface PublicFormState {
  status: PublicFormStatus;
  /** Set once `status` is `ready`. */
  form?: PublicEnquiryForm;
  /** Runs the read again — for the Try again a failed state offers. */
  reload: () => void;
}

export function usePublicEnquiryForm(handle: string): PublicFormState {
  const [status, setStatus] = useState<PublicFormStatus>("loading");
  const [form, setForm] = useState<PublicEnquiryForm>();
  // Bumped by `reload`, which is the whole of what re-runs the effect.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Nothing to ask for: the route matched, but on an empty segment.
    if (!handle.trim()) {
      setStatus("missing");
      return;
    }

    // Preview builds never touch the network — there is nothing serving this
    // endpoint yet. See `publicEnquiryPreview`.
    if (FORMS_PREVIEW) {
      setForm(PREVIEW_FORM);
      setStatus("ready");
      return;
    }

    const controller = new AbortController();
    setStatus("loading");

    fetchPublicEnquiryForm(handle, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setForm(loaded);
        setStatus("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        // A 404 is the ordinary case here rather than a fault — most links
        // that stop working stopped on purpose — so it is not logged.
        const notFound = error instanceof ApiError && error.status === 404;
        if (!notFound) console.error("Enquiry form failed to load:", error);
        setStatus(notFound ? "missing" : "failed");
      });

    return () => controller.abort();
  }, [handle, attempt]);

  const reload = useCallback(() => setAttempt((run) => run + 1), []);

  return { status, form, reload };
}

export default usePublicEnquiryForm;
